import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Spinner } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { completeSso, popIntendedUrl } from '@/lib/sso';
import { useAuth } from '@/auth/AuthProvider';

/**
 * Keycloak redirect target. Exchanges the returned `code` for tokens, persists
 * the session, then sends the user to their intended destination.
 */
export default function CallbackPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { setSession } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // guard StrictMode double-invoke (code is single-use)
    ran.current = true;

    const code = params.get('code');
    if (!code) {
      setError(t('common.error'));
      return;
    }
    completeSso(code)
      .then((tokens) => {
        if (!setSession(tokens)) throw new Error('Invalid session');
        navigate(popIntendedUrl(), { replace: true });
      })
      .catch(() => setError(t('common.error')));
  }, [params, navigate, setSession, t]);

  if (error) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh', padding: 'var(--vn-space-6)' }}>
        <Alert tone="danger" title={error} />
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh', gap: 'var(--vn-space-3)' }}>
      <Spinner size="lg" />
      <span style={{ color: 'var(--vn-fg-muted)' }}>{t('auth.loading')}</span>
    </div>
  );
}
